import type { PriceDetailsQueryParams } from '@/scopes/common/infrastructure/price-details/api/types'
import { useQuery } from '@tanstack/react-query'
import { type RefObject, useEffect } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { createSessionRecorder } from '@/scopes/products/domain/session-recorder'
import { recommendedAccosAPIClient } from '@/scopes/products/infrastructure/api/recommended-accos'
import { createSearchRecommendationInViewportEvent } from '@/scopes/search/search/domain/analytics'

export function useRecommendedAccos() {
  const accommodationId = useCMSPageField('PDP.id')
  const productId = useCMSPageField('PDP.productId')

  const { isLoading, data, isFetched } = useQuery({
    queryKey: ['recommended-accos', productId, accommodationId],
    queryFn: () => {
      const sessions = createSessionRecorder().getAllSessions()
      return recommendedAccosAPIClient.getRecommendedAccos({
        contextItemId: accommodationId,
        currentAccoId: productId,
        sessions,
      })
    },
  })

  return {
    isLoading,
    recommendedAccos: data,
    isFetched,
  }
}

export function useSessionRecorder() {
  const productId = useCMSPageField('PDP.productId')

  useEffect(() => {
    if (productId === null) {
      throw new Error('No productId provided to useSessionRecorder')
    }

    const sessionRecorder = createSessionRecorder()
    sessionRecorder.start(productId)

    // Stop the session recording when we lose the window focus (i.e. tab is changed)
    const onBlur = () => {
      sessionRecorder.stop(productId)
    }
    // Start again when we regain focus
    const onFocus = () => {
      sessionRecorder.start(productId)
    }

    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)

    return () => {
      sessionRecorder.stop(productId)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- we want to run this effect only when component is mounted
}

export function useAccoPriceDetails({
  accommodationId,
  departureDate,
  duration,
  mealPlan,
  participants,
  transportType,
}: PriceDetailsQueryParams) {
  const contextItemId = useCMSPageField('PDP.id')
  const priceDetailsApiClient = useCommonDependency('priceDetailsApiClient')

  const {
    isLoading,
    error,
    data: priceDetails,
  } = useQuery({
    queryKey: ['acco-price-details', accommodationId, contextItemId, departureDate, duration, mealPlan, transportType],
    queryFn: () => {
      return priceDetailsApiClient.getPriceDetails({
        contextItemId,
        accommodationId,
        departureDate,
        duration,
        mealPlan,
        participants,
        transportType,
      })
    },
  })

  return {
    isLoading,
    error,
    priceDetails,
  }
}

export function useSendRecommendationsInViewportEvent(ref: RefObject<HTMLElement | null>) {
  const isOnScreen = useOnScreen(ref)
  const { recommendedAccos, isLoading } = useRecommendedAccos()
  const { sendEvent } = useAnalytics()

  useEffect(() => {
    if (isOnScreen && !isLoading && recommendedAccos) {
      sendEvent(createSearchRecommendationInViewportEvent(recommendedAccos))
    }
  }, [isOnScreen, isLoading, recommendedAccos])
}
